import React, { useState } from "react";
import ScriptureContainer from "./ScriptureContainer";
import ScriptureItems from "./ScriptureItems";
import Popover from "./Popover";
import { isMobileDevice } from "../../utils/mobile";
import ScriptureRefModal from "./ScriptureRefModal";
import ErrorBoundary from "../UI/ErrorBoundary";
import { ScriptureItem } from "api/typedefs/ScriptureItem";
import { Theme } from "typedefs-ui/Theme";
import { ScriptDirection } from "api/typedefs/Bible";
import { useHighlights } from "components/Bible/ScriptureHighlights";
import { addVerseIconsToChapterContent } from "components/Feedback/tranformChapterContent";

interface ScriptureViewProps {
  bibleId: string;
  content: ScriptureItem[];
  scriptDirection?: ScriptDirection;
  currentChapterRef?: string;
  showVerseActions: boolean;
  highlightSelectedVerse?: boolean;
  disableHighlights?: boolean;
  disableFootnotes?: boolean;
  theme?: Theme;
}

const ScriptureView: React.FC<ScriptureViewProps> = ({
  bibleId,
  content,
  currentChapterRef = "",
  scriptDirection = "LTR",
  highlightSelectedVerse = false,
  showVerseActions = false,
  disableHighlights = false,
  disableFootnotes,
  theme
}) => {
  const { highlights, setHighlight, toggleHighlight } = useHighlights();

  const scriptureViewContent =
    content && !disableHighlights
      ? addVerseIconsToChapterContent(content, highlights.feedbacked)
      : content;
  const [popoverState, setPopOverState] = useState({
    chapterRef: "",
    position: {
      left: 0,
      top: 0
    }
  });
  const [refPreview, setRefPreview] = useState<string | null>("");

  const onVerseHover = (verseOrgIds: string[]) => {
    !disableHighlights && setHighlight("highlighted", verseOrgIds);
  };
  const onVerseUnhover = () => setHighlight("highlighted", []);

  const onVerseSelect = (
    verseOrgIds: string[],
    event: React.MouseEvent<HTMLElement>
  ) => {
    if (!disableHighlights) toggleHighlight("selected", verseOrgIds);

    if (showVerseActions) {
      const scriptureElem = document.getElementById("scripture") as HTMLElement;
      const containerLeft = scriptureElem.getBoundingClientRect().left;
      const verseTop = event.currentTarget.offsetTop;
      const verseLeft = event.clientX;

      setPopOverState({
        chapterRef: currentChapterRef,
        position: {
          // center on mobile, use position on screen in desktop
          left: isMobileDevice()
            ? window.innerWidth / 2 - 12 // -12 is size of padding of the scripture view container at mobile
            : verseLeft - containerLeft,
          top: verseTop
        }
      });
    }
  };

  const showRefPreview = (refId: string) => setRefPreview(refId);
  const hideRefPreview = () => setRefPreview(null);

  if (!scriptureViewContent) return null;

  return (
    <ErrorBoundary
      iconSize={100}
      style={{ margin: "40px auto", width: "100%" }}
    >
      <ScriptureContainer
        id="scripture"
        className="scripture-styles scripture-view"
        scriptDirection={scriptDirection}
        highlightSelectedVerse={highlightSelectedVerse}
        disableFootnotes={disableFootnotes}
        theme={theme}
      >
        <Popover
          bibleId={bibleId}
          currentChapterRef={currentChapterRef}
          selectedVerseOrgIds={highlights?.selected}
          {...popoverState}
        />
        <ScriptureItems
          highlights={highlights}
          onVerseHover={onVerseHover}
          onVerseSelect={onVerseSelect}
          onVerseUnhover={onVerseUnhover}
          showRefPreview={showRefPreview}
          items={scriptureViewContent}
        />
        {refPreview && (
          <ScriptureRefModal
            bibleId={bibleId}
            refId={refPreview}
            onClose={hideRefPreview}
          />
        )}
      </ScriptureContainer>
    </ErrorBoundary>
  );
};

export default ScriptureView;
